import type { Asset, AssetGroup, FancyCaptionAsset, TextAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { getObjectPropertyValue } from 'js/src/editor/util';
import { roundToPrecision } from 'js/src/libs/math';
import type {
    FilledTextGraphic,
    GroupBackgroundGraphic,
} from 'js/src/luminary/text-contrast-utils';
import {
    coversTheText,
    getFilledGroupColorGraphics,
    groupGraphicCoversTheText,
    isFilledTextGraphic,
    spansTheGlyphs,
} from 'js/src/luminary/text-contrast-utils';

import { renderColorAsString, renderColorSlot } from './assetHelpers';

/**
 * What a single colour on a text asset or animated caption IS, so the model reads a labelled legend
 * rather than inferring meaning from the order of two hex codes. The asset can carry far more colour
 * than its two glyph fills — each of its two styles (normal + a second style: highlighted runs for a
 * TextAsset, the spoken word for a FancyCaption) has a fill, an optional shadow, and a list of
 * box/bar graphics drawn behind the glyphs. An enclosing asset group can draw a backdrop behind the
 * whole thing too, but that is the GROUP's colour — it is addressed on the group's own map, not
 * folded in here. The model never sees the human colour picker, so this map is how it learns which
 * colour is which. How to CHANGE each one is taught by the tools and skills, not baked into this
 * state.
 *
 * `backdrop` is the box/panel that sits behind the glyphs (the `TextBackdrop` concept in
 * text-contrast-utils), whether the text draws it for itself or the group draws it for its children.
 */
export type TextColorRole =
    | 'text'
    | 'highlight'
    | 'spoken_word'
    | 'backdrop'
    | 'underline'
    | 'accent_bar'
    | 'box_outline'
    | 'shadow';

export interface TextColorDescription {
    /** Stable handle the model reads back to a tool (e.g. `text`, `highlight`, `box_1`). */
    handle: string;
    role: TextColorRole;
    /** Hex, with a ` [slot N]` suffix when it references a template colour slot. */
    value: string;
    /** 0-1. Present for box/panel/shadow roles when the fill is translucent. */
    alpha?: number;
    /** Present for box/panel roles. A `gradient` box ignores its flat fill/alpha at render time. */
    fillType?: 'solid' | 'gradient';
}

/**
 * A resolved colour: the public legend plus, for the box/panel entries an edit tool can write, how
 * to address the underlying graphic. Only the public fields are serialized.
 */
export interface TextColorHandle extends TextColorDescription {
    ownerAssetId?: string;
    valuePath?: string;
    alphaPath?: string;
}

/**
 * A text asset or a per-scene animated caption. Both carry a base `normalStyle` fill plus a second
 * style (a TextAsset's highlighted runs, a FancyCaption's spoken word) and can draw the same
 * boxes/bars behind their glyphs, so they share one colour map. The contrast-geometry helpers below
 * only read the shared `textSize`/`maxWidth`/`parent`, so a FancyCaption is passed to them as a
 * TextAsset.
 */
type ColorMapTextAsset = TextAsset | FancyCaptionAsset;

/**
 * The asset's "second" style — the one drawn on top of the base fill for some of the glyphs: a
 * TextAsset highlights runs via `highlightStyle`; a FancyCaption paints the spoken word via
 * `spokenWordStyle`. `shown` is whether that style actually paints anything on this asset, which
 * gates both its fill entry and its graphics in the sweep below.
 */
type SecondStyle = {
    styleName: 'highlightStyle' | 'spokenWordStyle';
    role: Extract<TextColorRole, 'highlight' | 'spoken_word'>;
    handle: string;
    shown: boolean;
};

function resolveSecondStyle(asset: ColorMapTextAsset): SecondStyle {
    if (VideoData.isFancyCaptionAsset(asset)) {
        return {
            styleName: 'spokenWordStyle',
            role: 'spoken_word',
            handle: 'spoken_word',
            shown: Boolean(asset.isSpokenWordStyleEnabled),
        };
    }
    return {
        styleName: 'highlightStyle',
        role: 'highlight',
        handle: 'highlight',
        shown: Boolean(asset.textHighlights?.length),
    };
}

const readInt = (asset: Asset, path: string): number => {
    const value = getObjectPropertyValue(asset, path);
    return typeof value === 'number' ? value : 0;
};

const readFillType = (asset: Asset, path: string): 'solid' | 'gradient' =>
    getObjectPropertyValue(asset, path) === 'linear' ? 'gradient' : 'solid';

const formatValue = (asset: Asset, path: string, includeColorNames: boolean): string =>
    `${renderColorAsString(readInt(asset, path), includeColorNames)}${renderColorSlot(asset, path)}`;

/**
 * A box or circle the text draws for itself reads as a full backdrop when it sits behind the glyphs;
 * a wide short box reads as an underline rule; anything narrower is an accent bar.
 */
function classifyTextGraphicRole(
    graphic: FilledTextGraphic,
    textAsset: ColorMapTextAsset,
): TextColorRole {
    if (coversTheText(graphic, textAsset as TextAsset)) {
        return 'backdrop';
    }
    if (graphic.type === VideoData.BOX_GRAPHIC) {
        const boxWidth = (graphic as { boxWidth?: unknown }).boxWidth;
        if (typeof boxWidth !== 'number' || spansTheGlyphs(boxWidth, textAsset as TextAsset)) {
            return 'underline';
        }
    }
    return 'accent_bar';
}

/**
 * A group draws a full backdrop, a thin underline rule, and a narrow accent bar all with the same
 * kind of shape — so classify by geometry against the group's representative text: covers the glyphs
 * → a `backdrop`; a wide short bar → an `underline`; anything narrower → an `accent_bar`.
 */
function classifyGroupGraphicRole(
    graphic: GroupBackgroundGraphic['graphic'],
    textAsset: ColorMapTextAsset,
): TextColorRole {
    if (groupGraphicCoversTheText(graphic, textAsset as TextAsset)) {
        return 'backdrop';
    }
    // Only a box reads as an underline rule. A circle shape has `radius`, not `boxWidth`, so it must
    // not fall through the symbolic-width path — a non-covering circle is a dot/ring, an accent_bar.
    if (graphic.graphic === VideoData.BOX_GRAPHIC) {
        const boxWidth = (graphic.shape as { boxWidth?: unknown }).boxWidth;
        if (typeof boxWidth !== 'number' || spansTheGlyphs(boxWidth, textAsset as TextAsset)) {
            return 'underline';
        }
    }
    return 'accent_bar';
}

/** The effective paint alpha: a gradient shape ignores its flat alpha and paints opaque. */
const effectiveAlpha = (fillType: 'solid' | 'gradient', storedAlpha: number): number =>
    fillType === 'gradient' ? 1 : storedAlpha;

/**
 * Every colour a text asset or per-scene animated caption carries, as an addressable, role-labelled
 * legend: its glyph fill and (when present) its second-style fill and shadow, and the boxes/bars it
 * draws behind its own glyphs across BOTH the normal and second styles. The second style is the
 * highlighted runs for a TextAsset and the spoken word for a FancyCaption (role `highlight` vs
 * `spoken_word`); both surface only when they actually paint on the asset. A backdrop an enclosing
 * group draws is the GROUP's colour, not the text's — see {@link buildGroupColorMap} — so it is
 * deliberately NOT folded in here.
 *
 * Never throws on a partially-built or mocked asset (serialization runs on every state capture).
 */
export function buildTextColorMap(
    textAsset: ColorMapTextAsset,
    includeColorNames: boolean = false,
): TextColorHandle[] {
    const handles: TextColorHandle[] = [];
    const value = (path: string): string => formatValue(textAsset, path, includeColorNames);
    const secondStyle = resolveSecondStyle(textAsset);

    // Glyph fill (base runs).
    const baseAlpha = readInt(textAsset, 'normalStyle._fillAlpha') || 1;
    handles.push({
        handle: 'text',
        role: 'text',
        value: value('normalStyle.fill'),
        ...(baseAlpha < 1 ? { alpha: roundToPrecision(baseAlpha, 2) } : {}),
    });

    // Second-style fill — only when it actually paints (highlighted runs / spoken-word style on).
    if (secondStyle.shown) {
        handles.push({
            handle: secondStyle.handle,
            role: secondStyle.role,
            value: value(`${secondStyle.styleName}.fill`),
        });
    }

    // Boxes/bars the text draws behind its own glyphs. The second style only paints on some glyphs,
    // so its graphics — like its fill above — are skipped when it doesn't show; otherwise a
    // second-style box would surface as a box_N that paints nothing on this asset.
    let boxNumber = 0;
    (['normalStyle', secondStyle.styleName] as const).forEach((styleName) => {
        if (styleName === secondStyle.styleName && !secondStyle.shown) {
            return;
        }
        const style = (textAsset as unknown as Record<string, unknown>)[styleName] as {
            graphics?: TextAsset['normalStyle']['graphics'];
        };
        (style.graphics ?? []).forEach((graphic, index) => {
            if (!isFilledTextGraphic(graphic) || !graphic.enabled || graphic.asMask) {
                return;
            }
            const prefix = `${styleName}.graphics.${index}`;
            const fillType = readFillType(textAsset, `${prefix}.fillType`);
            const alpha = effectiveAlpha(fillType, readInt(textAsset, `${prefix}.fillAlpha`));
            // A fully transparent box paints nothing — it is switched off in the design, so it is
            // not the agent's to flip on. Cull it rather than surfacing an addressable "hidden" box.
            if (alpha === 0) {
                return;
            }
            boxNumber += 1;
            const handle = `box_${boxNumber}`;
            handles.push({
                handle,
                role: classifyTextGraphicRole(graphic, textAsset),
                value: value(`${prefix}.fill`),
                alpha: roundToPrecision(alpha, 2),
                fillType,
                ownerAssetId: textAsset.id,
                valuePath: `${prefix}.fill`,
                alphaPath: `${prefix}.fillAlpha`,
            });
            // A visible outline is a distinct colour worth naming, even though no agent tool sets it.
            const strokeWidth = readInt(textAsset, `${prefix}.strokeWidth`);
            const strokeAlpha = readInt(textAsset, `${prefix}.strokeAlpha`);
            if (strokeWidth > 0 && strokeAlpha > 0) {
                handles.push({
                    handle: `${handle}_outline`,
                    role: 'box_outline',
                    value: value(`${prefix}.strokeColor`),
                    ...(strokeAlpha < 1 ? { alpha: roundToPrecision(strokeAlpha, 2) } : {}),
                });
            }
        });
    });

    // The base glyph's drop shadow, when the design draws one.
    if ((textAsset.normalStyle as { shadow?: boolean }).shadow) {
        const shadowAlpha = readInt(textAsset, 'normalStyle.shadowAlpha');
        handles.push({
            handle: 'shadow',
            role: 'shadow',
            value: value('normalStyle.shadowColor'),
            ...(shadowAlpha > 0 ? { alpha: roundToPrecision(shadowAlpha, 2) } : {}),
        });
    }

    return handles;
}

/**
 * Every colour an asset GROUP draws for itself: the backdrops / underlines / accent bars its own
 * `colorGraphics` paint behind its children, as an addressable, role-labelled legend — the group's
 * counterpart to {@link buildTextColorMap}. These used to be folded into every child text asset's
 * map, which surfaced one shared graphic once per child and addressed it through the wrong asset;
 * they belong on the group, which sits above the text in the tree.
 *
 * Geometry (backdrop vs underline vs accent bar) is judged against the group's representative text
 * child; a group with no text descendant falls back to `backdrop`. Never throws on a partially-built
 * or mocked group (serialization runs on every state capture).
 */
export function buildGroupColorMap(
    group: AssetGroup,
    includeColorNames: boolean = false,
): TextColorHandle[] {
    const representativeText = group
        .getAssets()
        .find(
            (child): child is TextAsset =>
                VideoData.isTextAsset(child) || VideoData.isFancyCaptionAsset(child),
        );

    const handles: TextColorHandle[] = [];
    getFilledGroupColorGraphics(group).forEach(({ index, graphic }) => {
        const prefix = `colorGraphics.${index}.shape`;
        const fillType = readFillType(group, `${prefix}.fillType`);
        const alpha = effectiveAlpha(fillType, readInt(group, `${prefix}.fillAlpha`));
        // A fully transparent backdrop paints nothing — switched off in the design, not the agent's
        // to flip on. Cull it rather than surfacing an addressable "hidden" box.
        if (alpha === 0) {
            return;
        }
        handles.push({
            handle: `box_${handles.length + 1}`,
            role: representativeText
                ? classifyGroupGraphicRole(graphic, representativeText)
                : 'backdrop',
            value: formatValue(group, `${prefix}.fillColor`, includeColorNames),
            alpha: roundToPrecision(alpha, 2),
            fillType,
            ownerAssetId: group.id,
            valuePath: `${prefix}.fillColor`,
            alphaPath: `${prefix}.fillAlpha`,
        });
    });
    return handles;
}

const toPublicColor = ({
    handle,
    role,
    value,
    alpha,
    fillType,
}: TextColorHandle): TextColorDescription => ({
    handle,
    role,
    value,
    ...(alpha !== undefined ? { alpha } : {}),
    ...(fillType !== undefined ? { fillType } : {}),
});

/** The public colour legend for a text asset's video-state entry (drops the write metadata). */
export function serializeTextColorMap(
    textAsset: ColorMapTextAsset,
    includeColorNames: boolean = false,
): TextColorDescription[] {
    return buildTextColorMap(textAsset, includeColorNames).map(toPublicColor);
}

/** The public colour legend for a group asset's video-state entry (drops the write metadata). */
export function serializeGroupColorMap(
    group: AssetGroup,
    includeColorNames: boolean = false,
): TextColorDescription[] {
    return buildGroupColorMap(group, includeColorNames).map(toPublicColor);
}
